import { ChangeEvent, FC, useEffect, useState } from 'react';
import { isMobileOnly } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { BsFillGrid3X3GapFill } from 'react-icons/bs';
import { FaThList } from 'react-icons/fa';
import { Button } from 'src/components/common/Button';
import { Input } from 'src/components/common/Input';
import { SvgIcon } from 'src/components/common/SvgIcon';
import { icons } from 'src/configs/icons';
import '../styles.scss';
import ListViewCard from './ListViewCard';

type ProviderListHolderViewProps = {
  providers: string[];
  selectedProvider: string[];
  onProviderChange: (provider: string | string[]) => void;
  togglePovidersFilterBtn: () => void;
  openProvidersList: boolean;
  selectedCategory: string;
  setOpenProvidersList: (open: boolean) => void;
};

const ProviderListHolderView: FC<ProviderListHolderViewProps> = (props) => {
  const { providers, selectedProvider, onProviderChange } = props;
  const { t }: Translation = useTranslation();

  const [search, setSearch] = useState<string>('');
  const [localProviders, setLocalProviders] = useState<string[]>([]);
  const [isOpen, setIsOpen] = useState<boolean>(!isMobileOnly);
  const [isGridView, setIsGridView] = useState<boolean>(true);
  const showClear = selectedProvider.length > 0 && !selectedProvider.includes('all_providers');
  const isAllProviders = (provider: string): boolean => provider.trim().toLowerCase() === 'all_providers';

  const onSearchValueChange = (ev: ChangeEvent<HTMLInputElement>): void => {
    const value = ev.target.value;
    setSearch(value);

    if (value.trim().length === 0) {
      setLocalProviders(providers.filter((provider) => !isAllProviders(provider)));
      return;
    }

    const query = value.trim().toLowerCase();
    setLocalProviders(
      providers.filter((provider) => !isAllProviders(provider) && provider.trim().toLowerCase().includes(query))
    );
  };

  useEffect(() => {
    setLocalProviders(providers.filter((provider) => !isAllProviders(provider)));
  }, [providers]);

  useEffect(() => {
    if (!isMobileOnly) return;
    const className = 'providers-only';
    if (isOpen) {
      document.body.classList.add(className);
    } else {
      document.body.classList.remove(className);
    }
    return () => document.body.classList.remove(className);
  }, [isOpen]);

  const toggleIcon = (() => {
    if (isMobileOnly) return icons.arrowLeft;
    return isOpen ? icons.arrowLeft : icons.arrowRight;
  })();

  const viewToggleIcon = isGridView ? <FaThList color="#fff" /> : <BsFillGrid3X3GapFill color="#fff" />;

  const renderProviderItem = (provider: string): JSX.Element | null => {
    if (isGridView) {
      return (
        <ListViewCard
          key={provider}
          t={t}
          provider={provider}
          selectedProvider={selectedProvider}
          onProviderChange={onProviderChange}
        />
      );
    }

    if (provider === 'all_providers') {
      return null;
    }

    return (
      <Button
        key={provider}
        className={`provider_list_item${
          Array.isArray(selectedProvider) && selectedProvider.includes(provider) ? ' selected' : ''
        }`}
        onClick={() => onProviderChange(provider)}
      >
        {provider}
      </Button>
    );
  };

  return (
    <div className={`providers_list_holder ${isOpen ? 'open' : 'collapsed'}`}>
      <Button className="providers_list_holder__toggle outside" onClick={() => setIsOpen((prev) => !prev)}>
        <SvgIcon src={toggleIcon} className="icon" />
        <span>{t('products')}</span>
      </Button>
      {isOpen && (
        <>
          <div className="providers_list_holder__search">
            <Input
              id="provider_list_holder_search"
              onChange={onSearchValueChange}
              value={search}
              name="provider_list_holder_search_input"
              placeholder={t('search_providers')}
              isTransparent
            />
            <div className="icon_wrapper">
              <SvgIcon src="/images/icons/svg/search.svg" className="icon" />
            </div>
          </div>
          <div className="providers_list_holder__actions">
            {showClear && (
              <>
                <span className="providers_list_holder__selected">
                  {selectedProvider.length} {t('selected')}
                </span>
                <Button className="providers_list_holder__clear" onClick={() => onProviderChange('all_providers')}>
                  <SvgIcon src={icons.remove} className="icon" />
                </Button>
              </>
            )}
            <Button className="providers_list_holder__view" onClick={() => setIsGridView((prev) => !prev)}>
              {viewToggleIcon}
            </Button>
          </div>
          <div className={`providers_list_holder__list ${isGridView ? 'grid' : 'list'}`}>
            {localProviders.map((provider) => renderProviderItem(provider))}
          </div>
        </>
      )}
    </div>
  );
};

export default ProviderListHolderView;
